import React from 'react';
import { Button as AriaButton, Link, ProgressBar } from 'react-aria-components';

import { tv } from '../../tv';
import { PressEvent, Size } from '../../types';
import { spinnerStyles } from '../spinner/spinner';
import { TooltipSeatContext } from '../tooltip/tooltip-seat-context';

/**
 * ComplyKit Button: the action control.
 *
 * The API is shaped by what the product's 315 Chakra Button tags actually do,
 * not by what a button could be:
 *
 * - The colour axis is variant + tone, never an open colorScheme: the census
 *   found buttons in exactly three colourings (brand accent, neutral chrome,
 *   red for destructive), so the palette knob is a one-value `tone` rather
 *   than a twelve-hue prop nobody uses eleven of.
 * - Pending is a first-class state: 31% of the product's buttons load, so the
 *   in-flight treatment (width-stable, focusable, announced) is part of the
 *   component, not a call-site spinner.
 * - Sizes are the 24/32/40 ramp with 32 as the default (12/14/16 text), the
 *   ramp Spectrum and Radix Themes ship: the product's own seats had asked
 *   for 32 a quarter of the time, so it is the default rather than an override.
 *
 * Element inference follows Card (interaction.md): `href` renders a real
 * link, otherwise it is a real button. There is no `as` prop. Spacing
 * between buttons is a layout concern (Stack, Flex, gap); fusing them into
 * one bar is ButtonGroup (button.md).
 */
interface ButtonCommonProps {
  /** The button's label. Required: a button with nothing on it is not a
   * button, and an icon-only action is IconButton. */
  children: React.ReactNode;
  /**
   * Visual emphasis, from the canonical ComplyKit variant vocabulary, and the
   * same axis the Figma component library uses. `solid` for the one primary
   * action of a region, `soft` for secondary actions, `outline` where the
   * action must read on any surface, `ghost` for toolbar-weight actions.
   */
  variant?: 'solid' | 'soft' | 'outline' | 'ghost';
  /**
   * Recolours any variant onto a semantic scale: `critical` (red) for a
   * destructive action, `ai` for an action the agent performs. Absent means
   * the brand accent for `solid` and neutral chrome for the rest. The union
   * grows only on evidence: the census found brand, neutral and red, and
   * `ai` joined when the ask-AI seats proved a fourth colouring that eleven
   * hand-rolled purples had been approximating.
   */
  tone?: 'critical' | 'ai';
  /** Control height: sm 24px, md 32px, lg 40px. md is the default and the
   * product's working density (button.md). */
  size?: Size;
  /**
   * Glyph rendered before the label: a heroicons 24/outline component, or a
   * custom SVG drawn in currentColor. The button sizes and colours it from
   * its own recipe (icon.md); the call site never does.
   */
  iconStart?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  /** Glyph rendered after the label. Same contract as `iconStart`. */
  iconEnd?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  /**
   * Whether the button is disabled. Prefer `isPending` for in-flight work:
   * disabling a focused button drops focus.
   *
   * Outside a Tooltip this is the native disabled attribute, exactly as
   * before. INSIDE a ComplyKit Tooltip a disabled button takes the
   * aria-disabled form instead: still focusable and hover-capable so the
   * tooltip that explains why it is disabled can open, with every press
   * suppressed and `type="submit"` rendered as `button` so it can never
   * submit a form (tooltip.md).
   */
  isDisabled?: boolean;
  /** Whether the button should receive focus on mount. */
  autoFocus?: boolean;
  /** The id of the button element. */
  id?: string;
  /** Accessible label, for the rare button whose visible label needs
   * overriding. An icon-only action is IconButton, not this. */
  'aria-label'?: string;
}

/* Somewhere to go OR something to do, and the type system holds the line: a
 * navigation cannot carry an in-page action, an in-flight state or a form
 * type, and an action cannot carry a target. The two forms are a union, so
 * the invalid combinations do not compile instead of being silently ignored
 * at runtime. */
interface ButtonLinkProps extends ButtonCommonProps {
  /**
   * Where the button goes. Renders a real link with the button's appearance,
   * so it can be opened in a new tab, copied, and reached by keyboard.
   * Navigation takes `href`; an in-page action takes `onPress`.
   */
  href: string;
  /** Link target, for the rare action that opens away from the app. */
  target?: '_blank';
  onPress?: never;
  isPending?: never;
  type?: never;
}

interface ButtonActionProps extends ButtonCommonProps {
  /**
   * What the button does: an action on the page rather than a navigation.
   * Presses do not bubble, and the event carries the modifier keys held
   * during the press (see `PressEvent`).
   */
  onPress?: (e: PressEvent) => void;
  /**
   * The in-flight state of the button's own action. The label and icons stay
   * in the box (hidden, so the width never moves) under a centred spinner;
   * the button stays focusable, exposes aria-disabled, ignores presses, and
   * announces progress to screen readers.
   */
  isPending?: boolean;
  /** The button's form behaviour. Defaults to `button`, so a button inside a
   * form never submits it by accident. */
  type?: 'button' | 'submit' | 'reset';
  href?: never;
  target?: never;
}

export type ButtonProps = ButtonLinkProps | ButtonActionProps;

/* The recipe is shared: IconButton renders the same chrome as a square
 * (shape: 'square'), so the two components cannot drift apart. Button owns
 * this config; icon-button.tsx imports it and adds nothing to it.
 *
 * Geometry is the 24/32/40 ramp (text 12/14/16, padding 8/12/16, weight
 * medium), heights on spacing steps 6 and 8 and the 40px control dimension,
 * so nothing off-scale enters. The radius is fixed at
 * rounded-3, deliberately without a prop: one shape of actionable, decided
 * once (the product-wide personality lives in the radius factor token).
 *
 * Colour states: hover moves one scale step; press SETTLES on the hover step
 * rather than descending further (interaction.md, the card's press
 * treatment), and the explicit pressed: classes are what give touch, where
 * hover never fires, its press feedback. All states ride React Aria's data
 * attributes via the plugin variants, so a tap can never leave hover stuck. */
export const buttonStyles = tv({
  slots: {
    root: [
      'ck:relative ck:inline-flex ck:items-center ck:justify-center',
      'ck:font-sans ck:font-medium ck:whitespace-nowrap ck:select-none',
      // Every variant carries the border so outline is not 2px wider.
      'ck:rounded-3 ck:border ck:border-transparent',
      'ck:cursor-pointer ck:transition-chrome',
      'ck:focus-visible:focus-ring',
      'ck:disabled:opacity-50 ck:disabled:cursor-not-allowed',
      'ck:pending:cursor-default',
    ],
    content: 'ck:inline-flex ck:items-center ck:gap-2',
    icon: 'ck:shrink-0',
    /* The overlay sits on the reserved box, so the spinner centres on the
     * hidden label and the button's width never moves while pending. */
    pendingOverlay: 'ck:absolute ck:inset-0 ck:flex ck:items-center ck:justify-center',
    /* Spinner's ring at the button's own steps: currentColor, so it follows
     * each variant's text for free. */
    spinner: '',
  },
  variants: {
    size: {
      sm: {
        root: 'ck:h-6 ck:px-2 ck:text-xs',
        content: 'ck:gap-1',
        icon: 'ck:size-3',
        spinner: spinnerStyles({ size: 'xs' }),
      },
      md: {
        root: 'ck:h-8 ck:px-3 ck:text-sm',
        icon: 'ck:size-4',
        spinner: spinnerStyles({ size: 'sm' }),
      },
      lg: {
        root: 'ck:h-10 ck:px-4 ck:text-base',
        icon: 'ck:size-4',
        spinner: spinnerStyles({ size: 'sm' }),
      },
    },
    /* IconButton's axis: a square of the same ramp, horizontal padding
     * replaced by a fixed width so the glyph centres optically. The width is
     * the shape, so a crowded row must not shrink it. */
    shape: {
      label: {},
      square: {
        root: 'ck:px-0 ck:shrink-0',
      },
    },
    /* Every colour reads the tone cascade (tokens.md): the root carries
     * data-tone, and these utilities resolve to whichever scale it names.
     * Nothing per tone lives here. */
    variant: {
      solid: {
        /* The solid is the tone's fill: its colour for every tone but ai, whose
         * fill is the signature deepened until white reads on it. */
        root: [
          'ck:bg-fill ck:text-tone-contrast',
          'ck:hover:bg-fill-hover ck:pressed:bg-fill-hover',
        ],
      },
      soft: {
        root: [
          'ck:bg-tone-a-3 ck:text-tone-text-soft',
          'ck:hover:bg-tone-a-4 ck:pressed:bg-tone-a-4',
        ],
      },
      outline: {
        root: [
          'ck:border-tone-border ck:text-tone-text',
          'ck:hover:bg-tone-a-2 ck:hover:border-tone-border-hover',
          'ck:pressed:bg-tone-a-2 ck:pressed:border-tone-border-hover',
        ],
      },
      ghost: {
        root: ['ck:text-tone-text', 'ck:hover:bg-tone-a-3 ck:pressed:bg-tone-a-3'],
      },
    },
    tone: {
      accent: {},
      neutral: {},
      critical: {},
      ai: {},
    },
    /* Driven by the isPending prop (which is exactly what data-pending
     * mirrors). Hidden, never unmounted: the label keeps its space, so the
     * width cannot move when the spinner appears. Opacity, never visibility
     * or display, for a second reason: opacity keeps the label in the
     * accessibility tree, which is what lets "Loading" join the accessible
     * name and the focused announcement fire; visibility: hidden or
     * display: none would silently break both. */
    pending: {
      true: { content: 'ck:opacity-0' },
      false: {},
    },
    /* The agent does not spin (ai.md): a pending AI-toned button keeps its
     * label, twinkles its glyph and orbits the signature round its edge. The
     * ProgressBar stays for the announcement; its overlay, moved onto the
     * border box, becomes the orbit's ring and the spinner's ring drops. */
    agentWorking: {
      true: {
        content: 'ck:opacity-100',
        icon: 'ck:animate-twinkle',
        pendingOverlay: 'ck:-inset-px ck:animate-orbit',
        spinner: 'ck:hidden',
      },
      false: {},
    },
    /* The aria-disabled form a disabled button takes inside a Tooltip seat
     * (tooltip.md). React Aria's pending machinery renders the behaviour
     * (focusable, tooltip hover events preserved, presses stripped, submit
     * coerced to type=button, aria-disabled exposed); these classes restate
     * only the disabled LOOK, because neither :disabled nor data-disabled is
     * present in this form for the disabled: variant to read. The cursor
     * rides the pending: variant so it outranks the base
     * pending:cursor-default in the merge, being declared later. */
    ariaDisabled: {
      true: { root: 'ck:opacity-50 ck:pending:cursor-not-allowed' },
      false: {},
    },
  },
  compoundVariants: [
    { size: 'sm', shape: 'square', class: { root: 'ck:w-6' } },
    { size: 'md', shape: 'square', class: { root: 'ck:w-8' } },
    { size: 'lg', shape: 'square', class: { root: 'ck:w-10' } },
    /* Neutral is chrome, not a hue: its label is the default text, where a
     * tone's label is its text step. The one pairing the cascade does not
     * express. */
    {
      tone: 'neutral',
      variant: ['soft', 'outline', 'ghost'],
      class: { root: 'ck:text-text-default' },
    },
    /* Icon-only buttons rest muted and darken on interaction: a bare glyph at
     * full text colour reads heavier than a labelled button, and the resting
     * chrome around content (table hover actions, card corners) should recede
     * until pointed at. The label shape keeps full colour; solid and critical
     * squares keep their own colours. Declared last so the muted rest wins
     * the text-colour merge against the variant pairings above. */
    {
      shape: 'square',
      tone: 'neutral',
      variant: ['soft', 'outline', 'ghost'],
      class: {
        root: ['ck:text-text-muted', 'ck:hover:text-text-default ck:pressed:text-text-default'],
      },
    },
  ],
  /* No defaultVariants: Button and IconButton pass every axis explicitly
   * (shape included), the same camp as checkbox/radio/switch, so there is no
   * second place a default could drift. */
});

/* An orbit on the solid AI fill would trace the signature over itself, so
 * the solid, like every other tone, spins while it waits. Shared with
 * IconButton. */
export function isAgentWorking(pending: boolean, tone: string, variant: string) {
  return pending && tone === 'ai' && variant !== 'solid';
}

/** The pending spinner, React Aria's prescribed pattern: an indeterminate
 * ProgressBar inside the pending button. The button provides it an id, folds
 * that id into its own labelling, and announces the transition to a focused
 * screen reader, so "Loading" joins the accessible name for exactly as long
 * as the work runs. Shared with IconButton only; not exported through the
 * barrel. */
export function ButtonSpinner({ overlay, disc }: { overlay: string; disc: string }) {
  return (
    <ProgressBar aria-label="Loading" isIndeterminate className={overlay}>
      <span className={disc} />
    </ProgressBar>
  );
}

export const Button = React.forwardRef<HTMLElement, ButtonProps>(function Button(
  {
    children,
    variant = 'solid',
    tone,
    size = 'md',
    iconStart: IconStart,
    iconEnd: IconEnd,
    isPending = false,
    isDisabled = false,
    onPress,
    href,
    target,
    type = 'button',
    ...props
  },
  ref,
) {
  /* Pending is the in-flight state of the button's own action; a link is a
   * navigation and has none, so the link render ignores it. */
  const pending = isPending && !href;

  /* Disabled inside a Tooltip seat takes the aria-disabled form: a natively
   * disabled button fires no interactions, so the tooltip explaining why it
   * is disabled could never open. React Aria's pending machinery IS that
   * form (it exists for tooltips: hover and focus events are preserved while
   * every press is stripped, submit is coerced to type=button, and
   * aria-disabled is exposed), so the seat rides it, with the pending
   * VISUALS staying off. Links are untouched: a disabled link-button is a
   * bare span with nothing to focus, exactly as documented in button.md. */
  const inTooltipSeat = React.useContext(TooltipSeatContext);
  const seated = isDisabled && inTooltipSeat && !href;

  /* Absent means the brand for solid and neutral chrome for the rest. */
  const resolvedTone = tone ?? (variant === 'solid' ? 'accent' : 'neutral');
  const { root, content, icon, pendingOverlay, spinner } = buttonStyles({
    variant,
    tone: resolvedTone,
    size,
    shape: 'label',
    pending,
    agentWorking: isAgentWorking(pending, resolvedTone, variant),
    ariaDisabled: seated,
  });

  const body = (
    <>
      <span className={content()}>
        {IconStart && <IconStart aria-hidden="true" focusable="false" className={icon()} />}
        {children}
        {IconEnd && <IconEnd aria-hidden="true" focusable="false" className={icon()} />}
      </span>
      {pending && <ButtonSpinner overlay={pendingOverlay()} disc={spinner()} />}
    </>
  );

  /* Somewhere to go or something to do, exactly as Card infers its element:
   * href renders a real link with the button's appearance, anything else is
   * a real button. The element, the keyboard behaviour and the focus ring
   * travel together; there is no `as` prop to get one of them wrong. */
  if (href) {
    return (
      <Link
        {...props}
        ref={ref as React.Ref<HTMLAnchorElement>}
        isDisabled={isDisabled}
        href={href}
        target={target}
        rel={target === '_blank' ? 'noopener noreferrer' : undefined}
        className={root()}
        data-component="button"
        data-tone={resolvedTone}
      >
        {body}
      </Link>
    );
  }

  return (
    <AriaButton
      {...props}
      ref={ref as React.Ref<HTMLButtonElement>}
      /* The seated form drops the native attribute and suppresses everything
       * itself: presses via the pending machinery, submission by rendering
       * type=button (belt and braces with React Aria's own pending
       * coercion). */
      isDisabled={seated ? undefined : isDisabled}
      onPress={seated ? undefined : onPress}
      type={seated && type === 'submit' ? 'button' : type}
      isPending={pending || seated}
      className={root()}
      data-component="button"
      data-tone={resolvedTone}
    >
      {body}
    </AriaButton>
  );
});
